"use client";

import * as React from "react";
import { cn } from "@repo/utils/cn";
import { Input } from "@repo/ui-parkmobile/Input";
import {
  useLocationAutocomplete,
  PlacePrediction,
  UseLocationAutocompleteOptions,
} from "@/hooks/useLocationAutocomplete";

export interface LocationSearchAutocompleteProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onPlaceSelect?: (place: PlacePrediction) => void;
  onSearch?: (value: string) => void;
  error?: boolean;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  searchOptions?: UseLocationAutocompleteOptions["searchOptions"];
}

export function LocationSearchAutocomplete({
  placeholder = "Where are you going?",
  value,
  onChange,
  onPlaceSelect,
  onSearch,
  error = false,
  className,
  inputClassName,
  dropdownClassName,
  searchOptions,
}: LocationSearchAutocompleteProps) {
  const {
    inputValue,
    setInputValue,
    predictions,
    showDropdown,
    selectedIndex,
    isLoaded,
    handleInputChange,
    handleKeyDown,
    handlePlaceSelect,
    containerRef,
  } = useLocationAutocomplete({
    searchOptions,
    onPlaceSelect: (place) => {
      onChange?.(place.description);
      onPlaceSelect?.(place);
    },
    onSearch,
  });

  React.useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e);
    onChange?.(e.target.value);
  };

  return (
    <div ref={containerRef} className={cn("uipm:relative", className)}>
      <Input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        state={error ? "error" : "default"}
        background="default"
        className={inputClassName}
      />

      {isLoaded && showDropdown && predictions.length > 0 && (
        <div
          className={cn(
            "uipm:absolute uipm:z-[9999] uipm:w-full",
            "uipm:mt-[var(--spacing-primitives-12)] uipm:rounded-xl",
            "uipm:bg-[var(--surface-surface-primary)] uipm:shadow-lg",
            "uipm:border-2 uipm:border-[var(--color-color-light)]",
            "uipm:max-h-[400px] uipm:overflow-y-auto",
            "uipm:scrollbar-thin uipm:scrollbar-thumb-gray-300 uipm:scrollbar-track-transparent",
            "hover:uipm:scrollbar-thumb-gray-400",
            dropdownClassName,
          )}
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgb(209 213 219) transparent",
          }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handlePlaceSelect(prediction)}
              className={cn(
                "uipm:w-full uipm:px-4 uipm:py-3",
                "uipm:text-left uipm:transition-colors uipm:cursor-pointer",
                "uipm:hover:bg-[var(--surface-surface-secondary)]",
                selectedIndex === index &&
                  "uipm:bg-[var(--surface-surface-secondary)]",
              )}
            >
              <div className="uipm:font-medium uipm:text-[var(--typography-colors-text-faded-dark)]">
                {prediction.structured_formatting.main_text}
              </div>
              {prediction.structured_formatting.secondary_text && (
                <div className="uipm:text-sm uipm:text-[var(--typography-colors-text-faded-dark)]">
                  {prediction.structured_formatting.secondary_text}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export type { PlacePrediction };
